import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/LoginPage';
import PublisherLoginPage from './pages/PublisherLoginPage';
import PortalLoginPage from './pages/PortalLoginPage';
import PageNotAccessible from './pages/PageNotAccessible';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ProjectsPage from './pages/ProjectsPage';
import PublishersPage from './pages/PublishersPage';
import GuestBlogSitesPage from './pages/GuestBlogSitesPage';
import PlanningPage from './pages/PlanningPage';
import OrdersPage from './pages/OrdersPage';
import DataEntryPage from './pages/DataEntryPage';
import SitesPage from './pages/SitesPage';
import GuestBlogPlacementsPage from './pages/GuestBlogPlacementsPage';
import InvoicesPage from './pages/InvoicesPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import AuditPage from './pages/AuditPage';
import PendingSitesPage from './pages/PendingSitesPage';
import PriceChangeRequestsPage from './pages/PriceChangeRequestsPage';
import PublisherBillingPage from './pages/PublisherBillingPage';
import AdminBillingPage from './pages/AdminBillingPage';
import ClientBillingPage from './pages/ClientBillingPage';
import LiveLinkStatusPage from './pages/LiveLinkStatusPage';
import SettingsPage from './pages/SettingsPage';
// Temporarily hidden: AI Suggestions & Competitor Analysis
// import DRCalculatorPage from './pages/DRCalculatorPage';
import ClientRequestedSitesPage from './pages/ClientRequestedSitesPage';
// import AISuggestionsPage from './pages/AISuggestionsPage';
import DrCalculatorSiteRequestsPage from './pages/DrCalculatorSiteRequestsPage';
import ProjectHistoricalUsagePage from './pages/ProjectHistoricalUsagePage';
import ProjectOverviewPage from './pages/ProjectOverviewPage';
import PlaceOrderPage from './pages/PlaceOrderPage';
import ProjectPlanWorkflowPage from './pages/ProjectPlanWorkflowPage';
import ProjectCartPage from './pages/ProjectCartPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import SuperAdminPasswordGate from './components/auth/SuperAdminPasswordGate';
import PermissionRoute from './components/auth/PermissionRoute';
import RestoreCenterPage from './pages/settings/RestoreCenterPage';

const GuestBlogSitesRoute: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const hasWorkflowContext = Boolean(search.get('projectId') || search.get('planId'));

  if (user?.role === 'AGENCY' && !hasWorkflowContext) {
    return <Navigate to="/dashboard" replace />;
  }
  return <GuestBlogSitesPage />;
};

function App() {
  const { user, loading, hasPermission, isSuperAdmin } = useAuth();

  const adminCan = (key: string) =>
    isSuperAdmin || (user?.role === 'ADMIN' && hasPermission(key));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LoadingSpinner />
      </Box>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<PageNotAccessible />} />
        <Route path="/login-administrator" element={<LoginPage />} />
        <Route path="/publisher" element={<PublisherLoginPage />} />
        <Route path="/login" element={<PortalLoginPage />} />
        <Route path="/client" element={<Navigate to="/login" replace />} />
        <Route path="/agency" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login-administrator" element={<Navigate to="/dashboard" replace />} />
        <Route path="/publisher" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/client" element={<Navigate to="/dashboard" replace />} />
        <Route path="/agency" element={<Navigate to="/dashboard" replace />} />

        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/project-workflow/overview" element={<ProjectOverviewPage />} />
        <Route path="/project-workflow/place-order" element={<PlaceOrderPage />} />
        <Route path="/project-workflow/plan" element={<ProjectPlanWorkflowPage />} />
        <Route path="/project-workflow/cart" element={<ProjectCartPage />} />

        {(user.role === 'AGENCY' || adminCan('clients')) && user.role !== 'PUBLISHER' && user.role !== 'CLIENT' && (
          <Route path="/clients/*" element={<ClientsPage />} />
        )}

        {user.role !== 'PUBLISHER' && (user.role === 'CLIENT' || user.role === 'AGENCY' || adminCan('projects')) && (
          <Route path="/projects/*" element={<ProjectsPage />} />
        )}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || adminCan('planning')) && (
          <Route path="/planning/*" element={<PlanningPage />} />
        )}

        {user.role === 'PUBLISHER' && <Route path="/publisher-billing" element={<PublisherBillingPage />} />}

        {adminCan('publishers') && <Route path="/publishers/*" element={<PublishersPage />} />}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || user.role === 'PUBLISHER' || adminCan('sites')) && (
          <Route path="/sites/*" element={<GuestBlogSitesRoute />} />
        )}

        {adminCan('historical_usage') && (
          <Route path="/projects/:projectId/historical-usage" element={<ProjectHistoricalUsagePage />} />
        )}

        {adminCan('pricing_sites') && <Route path="/pricing-sites/*" element={<SitesPage />} />}
        {adminCan('data_entry') && <Route path="/data-entry/*" element={<DataEntryPage />} />}

        {(user.role === 'CLIENT' || adminCan('orders')) && user.role !== 'AGENCY' && (
          <Route path="/orders/*" element={<OrdersPage />} />
        )}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || user.role === 'PUBLISHER' || adminCan('placements')) && (
          <Route path="/guest-blog-placements/*" element={<GuestBlogPlacementsPage />} />
        )}

        {(user.role === 'CLIENT' || user.role === 'AGENCY' || user.role === 'PUBLISHER' || adminCan('monitoring')) && (
          <Route path="/live-link-status/*" element={<LiveLinkStatusPage />} />
        )}

        {adminCan('reports') && <Route path="/reports/*" element={<ReportsPage />} />}

        {adminCan('archive') && <Route path="/archive" element={<RestoreCenterPage variant="self" />} />}

        {user.role === 'AGENCY' && (
          <>
            <Route path="/invoices/*" element={<ClientBillingPage />} />
            <Route path="/archive" element={<RestoreCenterPage variant="self" />} />
          </>
        )}

        {user.role === 'CLIENT' && (
          <>
            {/* AI Suggestions & Competitor Analysis temporarily hidden */}
            {/* <Route path="/competitor-backlink-analysis" element={<DRCalculatorPage />} /> */}
            {/* <Route path="/dr-calculator" element={<Navigate to="/competitor-backlink-analysis" replace />} /> */}
            {/* <Route path="/ai-suggestions" element={<AISuggestionsPage />} /> */}
            <Route path="/requested-sites" element={<ClientRequestedSitesPage />} />
            <Route path="/billing" element={<ClientBillingPage />} />
            <Route path="/archive" element={<RestoreCenterPage variant="self" />} />
          </>
        )}

        {adminCan('competitor_requests') && (
          <>
            <Route path="/competitor-backlink-site-requests" element={<DrCalculatorSiteRequestsPage />} />
            <Route path="/dr-calculator-site-requests" element={<Navigate to="/competitor-backlink-site-requests" replace />} />
          </>
        )}

        {adminCan('pending_sites') && <Route path="/pending-sites/*" element={<PendingSitesPage />} />}
        {adminCan('price_change') && <Route path="/price-change-requests" element={<PriceChangeRequestsPage />} />}

        {isSuperAdmin && (
          <Route
            path="/invoices/*"
            element={
              <SuperAdminPasswordGate>
                <InvoicesPage />
              </SuperAdminPasswordGate>
            }
          />
        )}
        {adminCan('invoices') && !isSuperAdmin && (
          <Route path="/invoices/*" element={<InvoicesPage />} />
        )}

        {adminCan('users') && <Route path="/users/*" element={<UsersPage />} />}
        {adminCan('audit') && <Route path="/audit/*" element={<AuditPage />} />}

        {isSuperAdmin && (
          <Route
            path="/admin-billing"
            element={
              <SuperAdminPasswordGate>
                <AdminBillingPage />
              </SuperAdminPasswordGate>
            }
          />
        )}
        {adminCan('admin_billing') && !isSuperAdmin && (
          <Route path="/admin-billing" element={<AdminBillingPage />} />
        )}

        {adminCan('settings') && <Route path="/settings/*" element={<SettingsPage />} />}

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
